import React, { ReactNode } from 'react';

import { Text } from '@rbilabs/universal-components';
import { differenceInYears, isExists, isFuture, isValid } from 'date-fns';

import Link from 'components/link';
import { routes } from 'navigation/routing';
import { t, tRich } from 'state/intl/translations';
import { useUIContext } from 'state/ui';
import { minimumSignupAge } from 'utils/environment';
import { Region } from 'utils/environment/types';
import { formatDateObject } from 'utils/form';
import { validationConfig } from 'utils/form/user-profile-validation-config/validation';
import formatListOfWords, { JoinType } from 'utils/intl/formatListOfWords';

const StyledText = Text.withConfig({
  variant: 'copyOne',
  fontSize: 'xs',
});

const LONGEST_RECORDED_HUMAN_LIFE_IN_YEARS = 122;

const BelowMinimumMessage = () => {
  const { setDialogStaticPageState } = useUIContext();

  const openDialogStaticPage = (page: string) => setDialogStaticPageState({ isOpen: true, page });

  return (
    <StyledText>
      {tRich('dateOfBirthIsBelowMinimum', {
        termsLink: (
          <Link isInline isUnderlined onPress={() => openDialogStaticPage(routes.termsOfService)}>
            <StyledText>{t('termsOfService')}</StyledText>
          </Link>
        ),
        privacyPolicyLink: (
          <Link isInline isUnderlined onPress={() => openDialogStaticPage(routes.privacyPolicy)}>
            <StyledText>{t('privacyPolicy')}</StyledText>
          </Link>
        ),
      })}
    </StyledText>
  );
};

const checkDob = (
  dobDate: Date,
  dobRawInput: { month: string; day: string; year: string }
): ReactNode | null => {
  // JS Date object sucks...
  dobDate.setTime(dobDate.getTime() + dobDate.getTimezoneOffset() * 60000);
  if (
    !isValid(dobDate) ||
    !isExists(
      parseInt(dobRawInput.year),
      parseInt(dobRawInput.month) - 1,
      parseInt(dobRawInput.day)
    )
  ) {
    return t('invalidDateOfBirthProvided');
  }
  if (isFuture(dobDate)) {
    return t('futureDateOfBirthProvided');
  }
  if (differenceInYears(Date.now(), dobDate) > LONGEST_RECORDED_HUMAN_LIFE_IN_YEARS) {
    return t('invalidDateOfBirthProvided');
  }
  if (differenceInYears(Date.now(), dobDate) < minimumSignupAge()) {
    return <BelowMinimumMessage />;
  }

  return null;
};

export const getDobErrors = (
  dob = { month: '', day: '', year: '' },
  region: Region
): ReactNode | null => {
  const isRequired = (() => {
    const req = validationConfig.dob.required;

    if (req === false) {
      return false;
    }

    return req === true || req.locales.includes(region);
  })();

  if (isRequired && !Object.values(dob).every(Boolean)) {
    return t('dateOfBirthIsRequired');
  }

  // Empty DOB (valid)
  if (!dob.month && !dob.day && !dob.year) {
    return null; // dob is not required
  }

  // Partial DOB
  if (!dob.month || !dob.day || !dob.year) {
    return getInvalidDateOfBirthMessage(dob);
  }

  return checkDob(new Date(formatDateObject(dob)), dob);
};

export const getLegacyDobErrors = (dob: string | undefined): null | ReactNode => {
  // Empty DOB (valid)
  if (!dob) {
    return null; // dob is not required
  }

  const [month, day, year] = dob.split('/');

  return checkDob(new Date(dob), { year, month, day });
};

function getInvalidDateOfBirthMessage(dob = { month: '', day: '', year: '' }) {
  const missingFieldList = [];
  if (!dob.year) {
    missingFieldList.push(t('year'));
  }
  if (!dob.month) {
    missingFieldList.push(t('month'));
  }
  if (!dob.day) {
    missingFieldList.push(t('day'));
  }

  const missingFields = formatListOfWords({
    list: missingFieldList,
    joinType: JoinType.CONJUCTION,
  });
  return t('invalidDateMissingFields', {
    count: missingFieldList.length,
    missingFields,
  });
}
